import React from 'react';
import bemBlock from '@gik/core/utils/bemBlock';
import FeaturesNavItem, { featuresScrollOffset, IFeaturesNavItemProps } from './FeaturesNavItem';
import { useTranslation } from 'react-i18next';
import { translationKeys } from './i18n/en';
import { getAssetsUrl } from '@gik/core/utils/LinkUtils';
import { Image } from '@gik/ui';
import { useLocation } from 'react-use';
import { scroller } from 'react-scroll';

export const FeaturesNavBlockName = 'features-nav';

export const calendarNavId = 'calendar';
export const wishlistNavId = 'wishlist';
export const updatesNavId = 'updates';
export const donationsNavId = 'donations';
export const moreNavId = 'more';

export default function FeaturesNav(): React.ReactElement {
  const bem = bemBlock(FeaturesNavBlockName);
  const { t } = useTranslation();

  const navItems: Omit<IFeaturesNavItemProps, 'activeKey' | 'setActive' | 'setInactive'>[] = [
    {
      variant: 'primary',
      to: calendarNavId,
      icon: (
        <Image
          style={{ height: '25px' }}
          src={[`${getAssetsUrl()}marketing/icons/calendar.png`, `${getAssetsUrl()}marketing/icons/calendar@2x.png 2x`]}
          alt={t(translationKeys.navCalendar)}
        />
      ),
      label: t(translationKeys.navCalendar),
    },
    {
      variant: 'danger',
      to: wishlistNavId,
      icon: (
        <Image
          className={bem('wishlist-icon')}
          style={{ height: '25px' }}
          src={[`${getAssetsUrl()}marketing/icons/wishlist.png`, `${getAssetsUrl()}marketing/icons/wishlist@2x.png 2x`]}
          alt={t(translationKeys.navWishlist)}
        />
      ),
      label: t(translationKeys.navWishlist),
    },
    {
      variant: 'secondary',
      to: updatesNavId,
      icon: (
        <Image
          style={{ height: '25px' }}
          src={[`${getAssetsUrl()}marketing/icons/updates.png`, `${getAssetsUrl()}marketing/icons/updates@2x.png 2x`]}
          alt={t(translationKeys.navUpdates)}
        />
      ),
      label: t(translationKeys.navUpdates),
    },
    {
      variant: 'success',
      to: donationsNavId,
      icon: (
        <Image
          style={{ height: '25px' }}
          src={[
            `${getAssetsUrl()}marketing/icons/fundraising.png`,
            `${getAssetsUrl()}marketing/icons/fundraising@2x.png 2x`,
          ]}
          alt={t(translationKeys.navFundraising)}
        />
      ),
      label: t(translationKeys.navFundraising),
    },
    {
      variant: 'warn',
      tint: '700',
      to: moreNavId,
      icon: (
        <Image
          style={{ height: '25px' }}
          src={[`${getAssetsUrl()}marketing/icons/sparkle.png`, `${getAssetsUrl()}marketing/icons/sparkle@2x.png 2x`]}
          alt={t(translationKeys.navMore)}
        />
      ),
      label: t(translationKeys.navMore),
    },
  ];

  const [activeKey, setActiveKey] = React.useState<string>(null);

  function setActive(id: string): void {
    setActiveKey(id);
  }

  function setInactive(id: string): void {
    // if the scroll leaves the bounds of one of the outer elements it means none of the sections are active
    if (id === 'calendar' && activeKey === 'calendar') {
      setActiveKey(null);
    }
    if (id === 'more' && activeKey === 'more') {
      setActiveKey(null);
    }
  }

  const location = useLocation();

  React.useEffect(() => {
    const item = navItems.find(item => item.to === location.hash.replace('#', ''));

    if (item) {
      scroller.scrollTo(item.to, {
        spy: true,
        smooth: true,
        offset: featuresScrollOffset,
      });
    }
  }, [location.hash]);

  return (
    <div className={bem()}>
      <ul>
        {navItems.map((item, i) => (
          <FeaturesNavItem activeKey={activeKey} setActive={setActive} setInactive={setInactive} key={i} {...item} />
        ))}
      </ul>
    </div>
  );
}
